<template>
  <div class="asc-related-mx-recs-grid">
    <asc-ag-grid-filter
      :key="`asc-related-mx-recs-grid_${gridId}`"
      :grid-options="agGridOptions"
      :column-defs="getGridColumnDefs"
      :row-data="agGridRowData"
      :grid-size="setGridSize"
    ></asc-ag-grid-filter>
  </div>
</template>

<script>
import {
  AscAgGridFilter,
  AscAgGridAutocompleteFloatFilter,
  AscAgGridDateFloatFilter,
  AscDrillDownCellRenderer,
  AscDrillDownReadCellRenderer,
  AscTooltipCellRenderer,
} from '@/components/agGrid'
import { AscGridPropsMixin, AscGridMethodsMixin } from '@/components/mixins'
import { getNewFormatUTCDateTime } from '@/utilities/dates'
import { MAINTENANCE_GRID_SORT_COLUMNS } from '@/utilities/appConsts'
import { getMaintenanceStatusValue } from '@/utilities/maintenance'
import {
  stringTextMatcher,
  UTCDateStringMatcher,
  exactEqualStringMatcher,
} from '@/components/agGrid/AgGridHelperMethods'
import { getFilterOptions } from '@/components/agGrid/AgGridOptions'
import { COLLINS_PHM_TIER_3, COLLINS_PHM_TIER_4 } from '@/utilities/appConsts'

export default {
  name: 'AscRelatedMxRecsGrid',

  components: {
    AscAgGridFilter,
    AscAgGridAutocompleteFloatFilter,
    AscAgGridDateFloatFilter,
    AscDrillDownCellRenderer,
    AscDrillDownReadCellRenderer,
    AscTooltipCellRenderer,
  },

  mixins: [AscGridPropsMixin, AscGridMethodsMixin],

  props: {
    aircraftDetails: {
      type: Object,
      default: () => {},
    },
  },

  data() {
    return {
      localStorageName: 'related-mx-recs-grid-ids',
      updateDateFieldName: 'lastAlertDate',
      agGridOptions: getFilterOptions(this, {
        defaultColDef: {
          tooltipComponent: 'AscTooltipCellRenderer',
        },
        floatingFiltersHeight: 45,
        headerHeight: 30,
        getContextMenuItems: this.getContextMenuItems,
        suppressNoRowsOverlay: true,
        onGridReady: this.handleGridReady,
        onFirstDataRendered: this.handleFirstDataRendered,
        onGridSizeChanged: this.handleGridSizeChanged,
        onCellClicked: this.handleCellClicked,
      }),
      statusColumnSortOrder: MAINTENANCE_GRID_SORT_COLUMNS,
    }
  },

  computed: {
    setGridSize() {
      return this.agGridRowData.length >= 5 ? 'sm' : 'auto'
    },
    mappedValues() {
      return this.values.map(item => {
        return {
          ...item,
          analyticModuleName:
            item.analyticModuleName === null || item.analyticModuleName === ''
              ? 'N/A'
              : item.analyticModuleName,
          systemName: item.systemName === null ? 'N/A' : item.systemName,
          componentName:
            item.componentName === null ? 'N/A' : item.componentName,
          ataChapterName:
            item.ataChapterName === null ? 'N/A' : item.ataChapterName,
          isAirlineTier3OrTier4:
            item.airlineTierTypeId === COLLINS_PHM_TIER_3 ||
            item.airlineTierTypeId === COLLINS_PHM_TIER_4
              ? true
              : false,
        }
      })
    },
    agGridRowData() {
      this.resetLocalStorage(this.localStorageName)

      return this.mappedValues
    },
    getGridColumnDefs() {
      return [
        {
          headerName: this.getColumnTitle(
            this.gridColumns,
            'issueId',
            'Issue ID',
          ),
          field: 'issueId',
          cellClass: ['ag-cell--drill', 'ag-cell--drill-ripple'],
          cellRenderer: 'AscDrillDownReadCellRenderer',
          cellRendererParams: {
            localStorageName: this.localStorageName,
            updateDateFieldName: this.updateDateFieldName,
          },
          filter: 'agNumberColumnFilter',
          hide: !this.isColumnVisible(this.gridColumns, 'issueId'),
        },
        {
          headerName: this.getColumnTitle(
            this.gridColumns,
            'statusName',
            'Status',
          ),
          field: 'statusName',
          filter: 'agTextColumnFilter',
          filterParams: {
            textMatcher: stringTextMatcher,
          },
          floatingFilterComponent: 'AscAgGridAutocompleteFloatFilter',
          floatingFilterComponentParams: {
            ids: this.getFloatFilterStatus(),
            items: this.getUniqueCustomSortItems(
              'statusName',
              this.statusColumnSortOrder,
            ),
            suppressFilterButton: true,
            suppressDefaultSort: true,
          },
          comparator: function (status1, status2) {
            const status1Value = getMaintenanceStatusValue(status1)
            const status2Value = getMaintenanceStatusValue(status2)
            return status2Value - status1Value
          },
          hide: !this.isColumnVisible(this.gridColumns, 'statusName'),
        },
        {
          headerName: this.getColumnTitle(
            this.gridColumns,
            'componentName',
            'Component',
          ),
          field: 'componentName',
          cellClass: this.isColumnDrillSupported(
            this.gridColumns,
            'componentName',
          )
            ? ['ag-cell--drill', 'ag-cell--drill-ripple']
            : null,
          cellRenderer: this.isColumnDrillSupported(
            this.gridColumns,
            'componentName',
          )
            ? 'AscDrillDownCellRenderer'
            : null,
          filter: 'agTextColumnFilter',
          filterParams: {
            textMatcher: exactEqualStringMatcher,
          },
          hide: true,
        },
        {
          headerName: this.getColumnTitle(
            this.gridColumns,
            'ataChapterName',
            'ATA',
          ),
          field: 'ataChapterName',
          cellClass: this.isColumnDrillSupported(
            this.gridColumns,
            'ataChapterName',
          )
            ? ['ag-cell--drill', 'ag-cell--drill-ripple']
            : null,
          cellRenderer: this.isColumnDrillSupported(
            this.gridColumns,
            'ataChapterName',
          )
            ? 'AscDrillDownCellRenderer'
            : null,
          filter: 'agTextColumnFilter',
          filterParams: {
            textMatcher: exactEqualStringMatcher,
          },
          hide: !this.isColumnVisible(this.gridColumns, 'ataChapterName'),
        },
        {
          headerName: this.getColumnTitle(
            this.gridColumns,
            'analyticModuleName',
            'Analytic',
          ),
          field: 'analyticModuleName',
          filter: 'agTextColumnFilter',
          filterParams: {
            textMatcher: exactEqualStringMatcher,
          },
          hide: !this.isColumnVisible(this.gridColumns, 'analyticModuleName'),
        },
        {
          headerName: this.getColumnTitle(
            this.gridColumns,
            'alertCount',
            'Alert Count',
          ),
          field: 'alertCount',
          filter: 'agNumberColumnFilter',
          hide: !this.isColumnVisible(this.gridColumns, 'alertCount'),
        },
        {
          headerName: this.getColumnTitle(
            this.gridColumns,
            'lastAlertDate',
            'Last Updated (UTC)',
          ),
          field: 'lastAlertDate',
          width: 155,
          minWidth: 155,
          sort: 'desc',
          filter: 'agTextColumnFilter',
          filterParams: {
            textMatcher: UTCDateStringMatcher,
          },
          floatingFilterComponent: 'AscAgGridDateFloatFilter',
          floatingFilterComponentParams: {
            suppressFilterButton: true,
            items: this.values,
            dateFloatFilterConfig: {
              left: true,
              transitionOrigin: 'top right',
            },
          },
          valueFormatter: data => getNewFormatUTCDateTime(data.value),
          comparator: this.sortDate,
          hide: !this.isColumnVisible(this.gridColumns, 'lastAlertDate'),
        },
      ]
    },
  },

  watch: {
    values() {
      this.handleRefreshColWidths()
    },
  },

  methods: {
    getFloatFilterStatus() {
      const array = []
      const status = this.$route.query.status
      if (status && status.length > 0) {
        array.push(status)
      }
      return array
    },

    handleCellClicked(event) {
      if (event) {
        const field = event.colDef.field
        if (field === 'issueId') {
          this.$router.push({
            name: 'Mx Recs',
            params: {
              issueId: event.value,
            },
          })
        }
      }
    },

    getContextMenuItems(params) {
      const issueId = params.node.data.issueId
      const url = `${issueId}`
      const result = [
        {
          name: 'Open Link in New Tab',
          action: function () {
            window.open(url, '_blank')
          },
        },
        'separator',
        'copy',
        'copyWithHeaders',
        'export',
      ]

      return result
    },
  },
}
</script>

<style lang="scss" scoped>
.asc-sort-icon {
  color: $primary_black;
  position: absolute;
  top: 14px;
  right: 16px;
}

.asc-filter-icon {
  color: $primary_black;
}
</style>
